import { DivCheckbox } from "@talladega/components";
import { translate } from "@talladega/helpers";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { ChangeEvent, ReactNode } from "react";
import { VehicleExteriorColour } from "@modules/vehicle/types/Vehicle";
import {
  ExteriorColourFilter,
  FilterAction,
  FilterActions,
  FilterKeys,
  VehicleFilter,
} from "../types/vehicleFilter";
import FilterDisclosure from "./FilterDisclosure";

type Props = {
  exteriorColours: VehicleExteriorColour[];
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
};

interface Orderable {
  order: number;
}

const orderFunction = (a: Orderable, b: Orderable) => a.order - b.order;

enum ExteriorColourEnum {
  White = 1,
  Black = 2,
  Silver = 3,
  Gray = 4,
  Beige = 5,
  Blue = 6,
  Brown = 7,
  Purple = 8,
  Yellow = 9,
  Orange = 10,
  Red = 11,
  Green = 12,
}

const VehicleExteriorColourFilter = ({
  exteriorColours,
  onUpdate,
  vehicleFilters,
}: Props) => {
  const { t } = useTranslation(["vehiclesFilters"]);
  const router = useRouter();
  const locale = router.locale || "";

  const exteriorColourFilter = vehicleFilters.filter(
    (f) => f.key === FilterKeys.ExteriorColour
  ) as ExteriorColourFilter[];

  const selectedExteriorColours = exteriorColourFilter.map((f) => f.state);

  const handleCheckChange = (
    event: ChangeEvent<HTMLInputElement>,
    displayExteriorColour: VehicleExteriorColour
  ) => {
    if (event.target.checked) {
      onUpdate({
        type: FilterActions.AddExteriorColourFilter,
        payload: {
          id: displayExteriorColour.id.toString(),
          name: displayExteriorColour.name,
        },
      });
    } else {
      onUpdate({
        type: FilterActions.RemoveByIdAndType,
        payload: {
          type: FilterKeys.ExteriorColour,
          id: displayExteriorColour.id.toString(),
        },
      });
    }
  };

  const setExteriorColourIcon = (id: number): ReactNode => {
    let colour = "";

    switch (id) {
      case ExteriorColourEnum.White: {
        colour = "bg-white border-solid border border-gray-100 ";
        break;
      }
      case ExteriorColourEnum.Black: {
        colour = "bg-black";
        break;
      }
      case ExteriorColourEnum.Silver: {
        colour = "bg-gray-50";
        break;
      }
      case ExteriorColourEnum.Gray: {
        colour = "bg-gray-200";
        break;
      }
      case ExteriorColourEnum.Beige: {
        colour = "bg-orange-200";
        break;
      }
      case ExteriorColourEnum.Blue: {
        colour = "bg-blue-500";
        break;
      }
      case ExteriorColourEnum.Brown: {
        colour = "bg-yellow-800";
        break;
      }
      case ExteriorColourEnum.Purple: {
        colour = "bg-purple-500";
        break;
      }
      case ExteriorColourEnum.Yellow: {
        colour = "bg-yellow-300";
        break;
      }
      case ExteriorColourEnum.Orange: {
        colour = "bg-orange-500";
        break;
      }
      case ExteriorColourEnum.Red: {
        colour = "bg-red-500";
        break;
      }
      case ExteriorColourEnum.Green: {
        colour = "bg-green-500";
        break;
      }

      default:
    }

    return <div className={classNames("w-8 h-8 rounded-full", colour)} />;
  };

  return (
    <FilterDisclosure
      headerText={t("vehiclesFilters:exterior_colour")}
      active={selectedExteriorColours.length > 0}
    >
      <ul className="body-3 grid grid-cols-2 gap-2">
        {exteriorColours &&
          exteriorColours.sort(orderFunction).map((ecs) => (
            <li key={ecs.id}>
              <DivCheckbox
                id={`exteriorColour${translate(
                  ecs.name,
                  locale || "en"
                )}Filter`}
                objectValue={ecs}
                className="flex-row"
                onChangeEvent={handleCheckChange}
                isChecked={
                  selectedExteriorColours
                    .map((x) => x.id)
                    .indexOf(ecs.id.toString()) > -1
                }
              >
                <span className="pr-2">{setExteriorColourIcon(ecs.id)}</span>
                <span className="w-20">{translate(ecs.name, locale)}</span>
              </DivCheckbox>
            </li>
          ))}
      </ul>
    </FilterDisclosure>
  );
};

export default VehicleExteriorColourFilter;
